import React from "react";
import {
  Box,
  Typography,
  TextField,
  FormHelperText,
  FormControl,
  Select,
  Chip,
  MenuItem,
  Checkbox,
} from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { SelectChangeEvent } from "@mui/material/Select";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";

interface FormFieldProps {
  label: string;
  name: string;
  value: any;
  disabled?: boolean;
  onChange?: (e: React.ChangeEvent<any> | SelectChangeEvent<any>) => void;
  placeholder?: string;
  error?: string;
  isSelect?: boolean;
  options?: { key: string; value: string }[];
  multiple?: boolean;
  handleDeleteChip?: (chip: string) => void;
  isChipsField?: boolean;
  renderValue?: (selected: any) => string;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
}

const renderErrorHelperText = (error: string | undefined) => {
  return error ? (
    <FormHelperText style={{ color: "#d32f2f" }}>{error}</FormHelperText>
  ) : null;
};

const webStyle = {
  fieldsTitle: {
    fontSize: "16px",
    fontWeight: "500",
    lineHeight: "22px",
    fontFamily: "Montserrat !important",
    marginBottom: "5px",
    "@media (max-width: 550px)": {
      fontSize: "14px",
      lineHeight: "18px",
    },
  },
  input: {
    fontSize: "16px",
    lineHeight: "24px",
    color: "#64748b !important",
    opacity: "1 !important",
    "& .MuiOutlinedInput-root": {
      borderRadius: "50px",
      borderColor: "#e2e8f0",
      "&:hover": { borderColor: "#cbd5e1" },
      "&.Mui-focused": {
        borderColor: "#673ab7",
        boxShadow: "0 0 0 3px rgba(103, 58, 183, 0.2)",
      },
    },
  },
};

const FormField: React.FC<FormFieldProps> = ({
  label,
  name,
  value,
  onChange,
  placeholder,
  error,
  disabled,
  isSelect = false,
  options = [],
  multiple = false,
  handleDeleteChip,
  isChipsField = false,
  inputProps,
}) => {
  return (
    <Box style={{ width: "100%" }} data-test-id="formContainer">
      <Typography sx={{ ...webStyle.fieldsTitle }}>{label}</Typography>

      {isSelect ? (
        <FormControl fullWidth sx={{ ...webStyle.input }}>
          <Select
            data-test-id="formFieldSelect"
            multiple={multiple}
            displayEmpty
            value={value}
            disabled={disabled}
            placeholder={placeholder}
            name={name}
            onChange={
              onChange as (
                event: SelectChangeEvent<any>,
                child: React.ReactNode
              ) => void
            }
            renderValue={(selected: any) => {
              if (isChipsField) {
                return (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {selected.map((language: any) => (
                      <Chip
                        data-test-id="language-chip"
                        key={language}
                        label={language}
                        onMouseDown={(event) => event.stopPropagation()}
                        onDelete={(event) => {
                          event.stopPropagation();
                          handleDeleteChip && handleDeleteChip(language);
                        }}
                        deleteIcon={
                          <CloseOutlinedIcon
                            style={{
                              color: "#0F172A",
                              height: "15px",
                              width: "15px",
                            }}
                          />
                        }
                        sx={{
                          borderRadius: "16px",
                          border: "1px solid #CBD5E1",
                          backgroundColor: "white",
                          padding: "5px 10px",
                        }}
                      />
                    ))}
                  </Box>
                );
              }
              return selected.length === 0
                ? "Choose language"
                : selected.join(", ");
            }}
            MenuProps={{
              sx: {
                "& .MuiPaper-root": {
                  width: "auto !important",
                  minWidth: "100%",
                },
              },
              PaperProps: {
                sx: {
                  maxHeight: 200,
                  overflowY: "auto",
                  minWidth: "unset",
                },
              },
            }}
            IconComponent={KeyboardArrowDownRoundedIcon}
          >
            <MenuItem value="" sx={{ display: value ? "none" : "block" }}>
              {placeholder || "Choose language"}
            </MenuItem>
            {options.map((option) => (
              <MenuItem key={option.key} value={option.value}>
                {multiple && (
                  <Checkbox checked={value.includes(option.value)} />
                )}
                {option.value}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : (
        <TextField
          fullWidth
          variant="outlined"
          placeholder={placeholder}
          name={name}
          value={value}
          disabled={disabled}
          onChange={onChange}
          sx={{ ...webStyle.input }}
          inputProps={inputProps}
        />
      )}

      {renderErrorHelperText(error)}
    </Box>
  );
};

export default FormField;
